import React from "react";
import { Heart, Wrench, Clock, Users } from "lucide-react";
import { TESTIMONIALS } from "../lib/constants";

const POINTS = [
  {
    icon: <Heart className="w-5 h-5 text-[#F5A623]" />,
    title: "Family-owned, not franchised",
    text:
      "Every job goes through our family. No subcontractor mystery, no call-center quotes.",
  },
  {
    icon: <Wrench className="w-5 h-5 text-[#F5A623]" />,
    title: "15+ years of trade experience",
    text:
      "Licensed and insured, with the right tools in the truck so we rarely need a second trip.",
  },
  {
    icon: <Clock className="w-5 h-5 text-[#F5A623]" />,
    title: "Show up when we say we will",
    text:
      "A text when we're on the way, a clean workspace when we leave. That's the whole promise.",
  },
  {
    icon: <Users className="w-5 h-5 text-[#F5A623]" />,
    title: "Neighbors, not strangers",
    text:
      "Local to Lehigh Acres and proud to serve the same families across Fort Myers and Cape Coral for years. Your neighbors already called us.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 border-t border-[#332C27] bg-gradient-to-b from-[#14110F] to-[#1a1614]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 reveal">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#F5A623] font-bold mb-4">
              / 02 — About us
            </div>
            <h2
              data-testid="about-heading"
              className="font-display text-4xl md:text-5xl lg:text-6xl text-[#F9FAFB] font-semibold uppercase tracking-tight leading-[1.05]"
            >
              Built by a
              <br />
              <span className="text-[#F5A623]">Lehigh Acres family,</span>
              <br />
              for SWFL homes.
            </h2>
            <p className="mt-8 text-[#A19D98] text-base md:text-lg leading-relaxed">
              We started 5-Star Handyman Service the way most family businesses
              start: a neighbor asked for a favor, and a friend told a friend.
              Today we're proud to take care of homes across Lehigh Acres and
              Fort Myers — from first-time homeowners to long-time Southwest
              Florida families.
            </p>
            <p className="mt-5 text-[#A19D98] text-base md:text-lg leading-relaxed">
              When you call, you get a real person. When we show up, you get
              clean boots, a clean truck, and a fair price — no pressure, no
              up-sells.
            </p>

            {/* Trust numbers */}
            <div className="mt-10 grid grid-cols-3 gap-4 border-t border-[#332C27] pt-8">
              <Stat k="500+" l="Projects completed" />
              <Stat k="15+" l="Years in the trade" />
              <Stat k="100%" l="5-star rated" />
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {POINTS.map((p, i) => (
              <div
                key={p.title}
                data-testid={`about-point-${i}`}
                className="reveal bg-[#1E1A18] border border-[#332C27] rounded-2xl p-7 hover:border-[#F5A623]/40 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-[#F5A623]/10 border border-[#F5A623]/20 flex items-center justify-center mb-4">
                  {p.icon}
                </div>
                <h3 className="font-display text-xl text-[#F9FAFB] font-semibold tracking-tight">
                  {p.title}
                </h3>
                <p className="text-[#A19D98] text-sm leading-relaxed mt-2">
                  {p.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="mt-20 md:mt-24">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#F5A623] font-bold mb-6">
            What neighbors say
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={i}
                data-testid={`testimonial-${i}`}
                className="reveal bg-[#1E1A18] border border-[#332C27] rounded-2xl p-7 hover:border-[#F5A623]/40 transition-colors"
              >
                <div className="flex gap-0.5 mb-4 text-[#F5A623] text-lg">
                  ★★★★★
                </div>
                <blockquote className="text-[#F9FAFB] leading-relaxed text-[15px]">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-6 text-sm">
                  <div className="text-[#F9FAFB] font-semibold">{t.name}</div>
                  <div className="text-[#A19D98] text-xs">{t.location}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ k, l }) {
  return (
    <div>
      <div className="font-display text-3xl md:text-4xl text-[#F5A623] font-bold tracking-tight">
        {k}
      </div>
      <div className="text-xs text-[#A19D98] uppercase tracking-[0.15em] mt-1">
        {l}
      </div>
    </div>
  );
}
